'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Real video player for the tube watch surface. Renders the Cinema8 embed
 * iframe (the actual playing surface — replaces the old "Cinema8 player: {id}"
 * placeholder), or a direct {@code <video>} for owned MP4s (shorts).
 *
 * <p>Host-agnostic: prefers {@code embedUrl} (built server-side from the
 * tenant's Cinema8 host so blocks never hardcode a domain); falls back to
 * building the URL from {@code mediaId} + {@code NEXT_PUBLIC_TUBE_CINEMA8_HOST}
 * (or {@link DEFAULT_CINEMA8_HOST}). Mirrors tube-v2's SSR fallback iframe
 * (`{host}/video/{id}?c=js-api&controls=1&resumeLastPosition=true`).</p>
 */
const DEFAULT_CINEMA8_HOST = 'https://cinema8.com';

export interface Cinema8PlayerProps {
  mediaId?: string;
  /** Pre-built, tenant-aware embed URL. Wins over {@code mediaId}. */
  embedUrl?: string;
  /** Direct MP4 (shorts / owned assets) — played via {@code <video>}. */
  videoUrl?: string;
  poster?: string;
  title?: string;
  autoplay?: boolean;
  style?: CSSProperties;
}

function buildEmbedUrl(mediaId: string, autoplay?: boolean): string {
  const envHost =
    typeof process !== 'undefined'
      ? (process.env.NEXT_PUBLIC_TUBE_CINEMA8_HOST as string | undefined)
      : undefined;
  const base = (envHost || DEFAULT_CINEMA8_HOST).replace(/\/+$/, '');
  const params = new URLSearchParams({
    c: 'js-api',
    autoplay: autoplay ? '1' : '0',
    controls: '1',
    resumeLastPosition: 'true',
    utm_source: 'tube',
  });
  return `${base}/video/${encodeURIComponent(mediaId)}?${params.toString()}`;
}

const frameStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  overflow: 'hidden',
  borderRadius: 'var(--radius-lg, 16px)',
  background: 'var(--color-neutral-950, #000)',
  boxShadow: 'var(--shadow-md, 0 10px 30px -12px rgba(0,0,0,0.45))',
};

export function Cinema8Player({
  mediaId,
  embedUrl,
  videoUrl,
  poster,
  title,
  autoplay,
  style,
}: Cinema8PlayerProps) {
  const { t } = useTranslation('site-builder-blocks');
  const frameTitle = title ?? t('cinema8.frameTitle');
  if (videoUrl) {
    return (
      <div style={{ ...frameStyle, ...style }}>
        <video
          src={videoUrl}
          poster={poster}
          controls
          {...(autoplay ? { autoPlay: true, muted: true } : {})}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            background: '#000',
          }}
        />
      </div>
    );
  }

  const src = embedUrl || (mediaId ? buildEmbedUrl(mediaId, autoplay) : null);
  if (!src) {
    return (
      <div style={{ ...frameStyle, display: 'grid', placeItems: 'center', ...style }}>
        <span style={{ color: 'var(--color-text-inverse, #fafafa)', opacity: 0.55, fontSize: 14 }}>
          {t('cinema8.noSource')}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        ...frameStyle,
        ...(poster
          ? {
              backgroundImage: `url(${poster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : {}),
        ...style,
      }}
    >
      <iframe
        title={frameTitle}
        src={src}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </div>
  );
}
